import React, { useState, useEffect } from 'react';
import { Container, Row } from 'react-bootstrap';

import './Team.css';
import logger from '@/shared/utils/logger';
import teamList from '@/assets/team/teams.json';
import sustainabilityLogo from '@/assets/img/logos/png/blueBackground.png';

import Leaders from './helper/Leaders';
import Researchers from './helper/Researchers';
import PastResearchers from './helper/PastResearchers';
import getGravatarData from './helper/gravatar';

export default function Team() {
    const [leadersList, setLeadersList] = useState([]);
    const [researchersList, setResearchersList] = useState([]);
    const [pastResearchersList, setPastResearchersList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGravatarData = async () => {
            try {
                const updatedTeamList = await Promise.all(
                    teamList.map(async (item) => {
                        const data = await getGravatarData(
                            item.profileIdentifier
                        );
                        return {
                            ...item,
                            display_name: data?.display_name,
                            avatar_url: data?.avatar_url,
                            profile_url: data?.profile_url,
                        };
                    })
                );

                // A researcher row missing the `current` flag defaults to the
                // current list; otherwise a row without the field would be
                // silently dropped from both the current and past sections.
                const filterAndSort = (members, current) =>
                    members
                        .filter(
                            (member) => (member.current ?? true) === current
                        )
                        .sort((a, b) => {
                            const lastNameA = a.name
                                .split(' ')
                                .slice(-1)[0]
                                .toLowerCase();
                            const lastNameB = b.name
                                .split(' ')
                                .slice(-1)[0]
                                .toLowerCase();
                            return lastNameA.localeCompare(lastNameB);
                        });

                const filteredLeaders = updatedTeamList.filter(
                    (member) => member.leader === true
                );
                const filteredResearchers = updatedTeamList.filter(
                    (member) => member.leader === false
                );
                const currentResearchers = filterAndSort(
                    filteredResearchers,
                    true
                );
                const pastResearchers = filterAndSort(
                    filteredResearchers,
                    false
                );

                setLeadersList(filteredLeaders);
                setResearchersList(currentResearchers);
                setPastResearchersList(pastResearchers);
            } catch (error) {
                // Gravatar enrichment is best-effort. A failed lookup must not
                // throw into the fire-and-forget effect promise (that produced
                // an unhandled rejection); log it and let `finally` clear the
                // loading state so the page still renders the JSON roster.
                logger.error('Error fetching Gravatar data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGravatarData();
    }, []);

    if (loading) {
        return <div>Loading team data...</div>;
    }

    return (
        <section id="team">
            <Container className="team-container">
                <Row className="title1-row">
                    <div className="draw-line " />
                    <h1 className="fs-1 text-primary my-5">Meet the Team</h1>
                </Row>
                <Row className="leaders-row">
                    {leadersList.map((leader, index) => (
                        <Leaders
                            key={index}
                            imgSrc={leader?.avatar_url || sustainabilityLogo}
                            name={leader?.display_name || leader?.name}
                            link={leader?.profile_url}
                            title={leader?.title}
                            university={leader?.university}
                            timeline={leader?.timeline}
                        />
                    ))}
                </Row>
                <Row className="title2-row py-3">
                    <div className="text-primary">
                        <span>and our </span>
                        <h2 className="h4">Student Researchers</h2>
                    </div>
                </Row>
                <Row className="researchers-row px-5">
                    {researchersList.map((researcher, index) => (
                        <Researchers
                            key={index}
                            imgSrc={
                                researcher?.avatar_url || sustainabilityLogo
                            }
                            name={researcher?.display_name || researcher?.name}
                            link={researcher?.profile_url}
                            title={researcher?.title}
                            university={researcher?.university}
                            timeline={researcher?.timeline}
                        />
                    ))}
                </Row>
                <Row className="pastResearchers-row justify-content-center px-5">
                    <div className="line-text mb-lg-3 text-center">
                        <span className="line" />
                        <span className="px-1 line-text-content">
                            Past Researchers
                        </span>
                        <span className="line" />
                    </div>
                    <PastResearchers
                        pastResearchersList={pastResearchersList}
                        sustainabilityLogo={sustainabilityLogo}
                    />
                    <div className="my-5 line" />
                </Row>
            </Container>
        </section>
    );
}
